<template>
  <div
    class="adm-modal__backdrop"
    role="dialog"
    aria-modal="true"
    :aria-label="title"
    @click.self="onBackdrop"
  >
    <div class="adm-modal" :class="`adm-modal--${size}`" ref="panel">
      <header class="adm-modal__head">
        <h3 class="adm-subtitle">{{ title }}</h3>
        <button type="button" class="adm-modal__close" aria-label="Cerrar" @click="$emit('close')">
          <X :size="18" />
        </button>
      </header>

      <div class="adm-modal__body adm-scroll">
        <slot />
      </div>

      <footer v-if="$slots.footer" class="adm-modal__foot">
        <slot name="footer" />
      </footer>
    </div>
  </div>
</template>

<script>
/**
 * Modal del panel — plan 27_09_2026 §4.2.
 *
 * Base común para los diálogos administrativos (el primero,
 * `OrdenDetalleModal`, traía su propio backdrop y su propia caja).
 * Cierra con Escape y al pulsar fuera, y devuelve el foco al salir.
 */
import { onMounted, onBeforeUnmount, ref } from 'vue';
import { X } from 'lucide-vue-next';

export default {
  name: 'AdmModal',
  components: { X },
  props: {
    title: { type: String, default: '' },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg'].includes(v),
    },
    /** Con `persistent`, pulsar fuera no cierra (para formularios con datos). */
    persistent: { type: Boolean, default: false },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const panel = ref(null);
    // Se recuerda quién tenía el foco para devolvérselo al cerrar: si no,
    // al salir del modal el foco vuelve al principio de la página.
    let previouslyFocused = null;

    function onKeydown(event) {
      if (event.key === 'Escape') emit('close');
    }

    function onBackdrop() {
      if (!props.persistent) emit('close');
    }

    onMounted(() => {
      previouslyFocused = document.activeElement;
      document.addEventListener('keydown', onKeydown);
      panel.value?.focus?.();
    });

    onBeforeUnmount(() => {
      document.removeEventListener('keydown', onKeydown);
      previouslyFocused?.focus?.();
    });

    return { panel, onBackdrop, X };
  },
};
</script>

<style scoped>
.adm-modal__backdrop {
  position: fixed;
  inset: 0;
  z-index: var(--adm-z-modal);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--adm-content-pad);
  background-color: rgba(44, 62, 80, 0.55);
}

.adm-modal {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: calc(100vh - 2 * var(--adm-content-pad));
  background-color: var(--adm-surface);
  border-radius: var(--adm-radius);
  box-shadow: var(--adm-shadow-lg);
  overflow: hidden;
}

.adm-modal--sm { max-width: 420px; }
.adm-modal--md { max-width: 640px; }
.adm-modal--lg { max-width: 940px; }

.adm-modal__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--adm-gap);
  padding: var(--adm-gap) var(--adm-gap-lg);
  border-bottom: 1px solid var(--adm-border);
}

.adm-modal__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  background: none;
  border: none;
  border-radius: var(--adm-radius-sm);
  color: var(--color-text);
  cursor: pointer;
}

.adm-modal__close:hover { background-color: var(--adm-surface-alt); color: var(--color-title); }

.adm-modal__body {
  padding: var(--adm-gap-lg);
  /* Recorta por necesidad: el diálogo tiene altura máxima y su contenido
     scrollea. Un desplegable flotante puesto aquí dentro quedaría cortado;
     los `select` nativos no, porque el navegador los pinta fuera del flujo. */
  overflow-y: auto;
}

.adm-modal__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: var(--adm-gap-sm);
  flex-wrap: wrap;
  padding: var(--adm-gap) var(--adm-gap-lg);
  border-top: 1px solid var(--adm-border);
  background-color: var(--adm-surface-alt);
}
</style>
